import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, User, Loader2, ArrowUpRight } from "lucide-react";
import { CONTACT, SERVICES } from "@/data/site";
import { Reveal } from "@/lib/motionPrimitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your inquiry has been sent. We'll get back to you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: User, label: "Contact Person", value: CONTACT.person },
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: "Location", value: CONTACT.address },
  ];

  return (
    <section id="contact" className="relative py-24 md:py-32" data-testid="contact">
      <div className="container-x">
        <Reveal>
          <p className="overline">Get In Touch</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Request a Quote
          </h2>
          <p className="mt-4 max-w-xl text-muted-foreground">
            Tell us about your structure, area and location — we&apos;ll prepare a durable, cost-effective quotation.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-12">
          {/* Info panel */}
          <div className="lg:col-span-5">
            <div className="h-full rounded-3xl bg-primary p-8 text-primary-foreground md:p-10">
              <h3 className="font-display text-xl font-bold">Contact Details</h3>
              <div className="mt-8 space-y-6">
                {details.map((d) => {
                  const Cmp = d.icon;
                  const content = (
                    <div className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
                        <Cmp className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="text-xs uppercase tracking-widest text-primary-foreground/70">{d.label}</p>
                        <p className="mt-1 font-medium leading-snug">{d.value}</p>
                      </div>
                    </div>
                  );
                  return d.href ? (
                    <a key={d.label} href={d.href} className="block transition-opacity hover:opacity-80" data-testid={`contact-info-${d.label}`}>
                      {content}
                    </a>
                  ) : (
                    <div key={d.label} data-testid={`contact-info-${d.label}`}>{content}</div>
                  );
                })}
              </div>

              <a
                href={`https://wa.me/${CONTACT.phoneRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-primary transition-transform hover:scale-[1.03]"
              >
                Chat on WhatsApp <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 rounded-3xl border border-border bg-surface p-8 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" data-testid="input-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" data-testid="input-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input id="phone" data-testid="input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 ..." />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Service Required</Label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="select-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" data-testid="input-message" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Describe your requirement, structure type, area & location" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary mt-6 w-full sm:w-auto disabled:opacity-70" data-testid="contact-submit-button">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send Inquiry <ArrowUpRight className="h-4 w-4" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
